<script setup lang="ts">
import { computed, ref } from 'vue';

import IconChevron from '@/shared/components/UI/Icons/IconChevron.vue';
import { type KitItemInput, useKits } from '@/shared/composables/useKits';
import { useTranslations } from '@/shared/composables/useTranslations';

import KitItemRow from './KitItem.vue';

export type KitItemData = KitItemInput;

const props = defineProps<{
  items: KitItemData[];
}>();

const { t } = useTranslations();
const isOpen = ref(true);

const itemsRef = computed(() => props.items);
const { resolvedItems, loading } = useKits(itemsRef);
</script>

<template>
  <div v-if="items.length > 0" class="mt-1">
    <button
      type="button"
      class="my-2 inline-flex items-center gap-0.5 text-xxs text-gray-500 transition-colors hover:text-gray-700"
      @click="isOpen = !isOpen"
    >
      <span>{{
        isOpen
          ? t('cart.kit_hide_items', { count: items.length })
          : t('cart.kit_show_items', { count: items.length })
      }}</span>
      <IconChevron :direction="isOpen ? 'up' : 'down'" class="text-xs" />
    </button>

    <div
      class="grid transition-[grid-template-rows] duration-200 ease-in-out"
      :class="isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
    >
      <ul
        class="min-h-0 overflow-hidden pl-2 [&>*:first-child]:mt-1 [&>*:last-child]:mb-1"
      >
        <template v-if="loading">
          <li
            v-for="n in items.length"
            :key="n"
            class="flex items-center gap-2 py-1"
          >
            <div class="size-8 shrink-0 animate-pulse rounded bg-gray-100" />
            <div class="h-3 w-24 animate-pulse rounded bg-gray-100" />
          </li>
        </template>
        <template v-else>
          <KitItemRow
            v-for="(kitItem, idx) in resolvedItems"
            :key="idx"
            class="py-1"
            :image="kitItem.image"
            :quantity="kitItem.quantity"
            :title="kitItem.title"
            :variant-title="kitItem.variantTitle"
          />
        </template>
      </ul>
    </div>
  </div>
</template>
